// src/components/PassengerHome.js
// MUDANÇAS v3.2:
//  1. Importa ModalPixAntecipado sem presumir confirmação bancária
//  2. Popup "pagar Pix agora ou ao final?" ao iniciar corrida (in_progress + pix)
//  3. Marcador do motorista se movendo no MapComponent
//  4. Passa qrCodePix do dadosMotorista ao ModalPagamento final

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, rtdb } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { isStoreScreenshotCapture, storeScreenshotUser } from "../screenshotSession";
import { doc, getDoc } from "firebase/firestore";
import { ref as rtdbRef, onValue } from "firebase/database";
// O mapa principal e o seletor de origem compartilham a identidade dark e a mesma câmera responsiva.
import Map, { getZunoMapStyles } from "../components/MapComponent";
import HomePromoCarousel, { getIOSHomeCampaigns } from "../components/HomePromoCarousel";
import PassengerAvatar from "../components/PassengerAvatar";
import EscolhaDestino from "../pages/EscolhaDestino";
import BottomNav from "../components/BottomNav";
import RatingModal from "../components/RatingModal";
import WelcomeScreen from "../components/WelcomeScreen";
import PaymentSelector from "../components/PaymentSelector";
import { authorizeZunoCardRide } from "../services/zunoCardService";
import ChatCorrida from "../components/ChatCorrida";
import ModalMotoristaChegou from "../components/ModalMotoristaChegou";
import ModalPagamento from "../components/ModalPagamento";
import ModalPixCorrida from "../components/ModalPixCorrida";
import ModalPixAntecipado from "../components/ModalPixAntecipado";
import SOSButton from "../components/SOSButton";
import { LuBanknote, LuBell, LuCalendarDays, LuCheck, LuChevronDown, LuCircleAlert, LuCreditCard, LuCrosshair, LuLink, LuMenu, LuMessageCircle, LuQrCode, LuScissors, LuShare2, LuShieldCheck, LuStar, LuUserRound, LuUsersRound, LuX } from "react-icons/lu";
import { ZunoVehicle } from "../components/ZunoVisuals";
import zunoSymbol from "../assets/zuno-splash.png";
import driverPartnerBanner from "../assets/zuno/motorista-parceiro-banner-v1.png";
import driverPartnerLightBanner from "../assets/zuno/driver-partner-light.png";
import {
  criarCorrida, escutarCorrida, cancelarCorrida, avaliarMotorista,
  escutarCorridasAgendadas, cancelarCorridaAgendada,
  cancelarCorridaComMotivo,
} from "../services/rideService";
import { inicializarNotificacoes, escutarNotificacoesForeground } from "../services/notificationService";
import { criarShareLink } from "../services/shareService";
import { useTheme } from "../theme/ThemeContext";
import { getCachedProfilePhoto, setCachedProfilePhoto, subscribeToProfilePhoto } from "../services/profileStore";
import { distanceInMeters, reverseGeocode } from "../services/geocodingService";
import { normalizeBrazilianPhone, persistPhoneAndResumeRide } from "../services/passengerPhoneService";
import { joinCityInterest } from "../services/legalService";
import { getVisibleViewportMetrics } from "../utils/visualViewport";
import { checkLocationPermission, getCurrentLocation, persistLocationState, watchLocation } from "../services/locationService";
import { markPerformance } from "../services/performanceTrace";
import { bindClientStorageToUser, readAuthScopedClientValue } from "../services/clientStorageSecurity";

// ─── MARCADOR GPS DO PASSAGEIRO ───────────────────────────────
// ─── MAPA PARA ESCOLHER DESTINO COM PIN CENTRAL ──────────────
function MapEscolhedor({ userPos, onCenterChange }) {
  const { theme } = useTheme();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const listenerRef = useRef(null);

  useEffect(() => {
    const init = () => {
      if (!window.google?.maps || mapInstanceRef.current) return;
      const center = userPos ? { lat: userPos.lat, lng: userPos.lng } : { lat: -26.4833, lng: -49.0667 };
      const map = new window.google.maps.Map(mapRef.current, {
        center, zoom: 16, gestureHandling: "greedy",
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
        zoomControl: true, zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
        styles: getZunoMapStyles(theme),
      });
      mapInstanceRef.current = map;
      onCenterChange?.(center.lat, center.lng);
      listenerRef.current = map.addListener("center_changed", () => {
        const c = map.getCenter();
        onCenterChange?.(c.lat(), c.lng());
      });
    };
    if (window.google?.maps) { init(); return; }
    const iv = setInterval(() => { if (window.google?.maps) { clearInterval(iv); init(); } }, 300);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mapInstanceRef.current?.setOptions({ styles: getZunoMapStyles(theme) });
  }, [theme]);

  useEffect(() => {
    return () => {
      if (listenerRef.current) window.google?.maps?.event?.removeListener(listenerRef.current);
    };
  }, []);

  return <div ref={mapRef} style={{ position:"absolute", inset:0 }} />;
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────
function PassengerHome({ showSessionIntro = false, onSessionIntroContinue }) {
  markPerformance("HOME_RENDER_START", {}, { onlyOnce: true });
  const { theme } = useTheme();
  const navigate = useNavigate();
  const homeScreenRef = useRef(null);
  const initialUid = auth.currentUser?.uid || null;
  const initialRideId = readAuthScopedClientValue(initialUid, "activeRideId");

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const viewport = window.visualViewport;
    const syncViewportHeight = () => {
      const height = Math.round(viewport?.height || window.innerHeight);
      if (height > 0) root.style.setProperty("--zuno-home-viewport-height", `${height}px`);
    };

    root.classList.add("zuno-passenger-home-active");
    body.classList.add("zuno-passenger-home-active");
    syncViewportHeight();
    viewport?.addEventListener("resize", syncViewportHeight);
    window.addEventListener("resize", syncViewportHeight);
    window.addEventListener("orientationchange", syncViewportHeight);

    return () => {
      viewport?.removeEventListener("resize", syncViewportHeight);
      window.removeEventListener("resize", syncViewportHeight);
      window.removeEventListener("orientationchange", syncViewportHeight);
      root.classList.remove("zuno-passenger-home-active");
      body.classList.remove("zuno-passenger-home-active");
      root.style.removeProperty("--zuno-home-viewport-height");
    };
  }, []);

  useEffect(() => {
    markPerformance("HOME_RENDER_DONE", {}, { onlyOnce: true });
  }, []);
  const [destino, setDestino] = useState(null);
  const [openPainel, setOpenPainel] = useState(false);
  const [addressSearchActive, setAddressSearchActive] = useState(false);
  const [addressViewport, setAddressViewport] = useState(() => (
    getVisibleViewportMetrics(window.visualViewport, window.innerHeight)
  ));
  const [usuario, setUsuario] = useState("");
  const [fotoUsuario, setFotoUsuario] = useState("");
  const [userUid, setUserUid] = useState(null);
  const [dadosRota, setDadosRota] = useState({ km: 0, min: 0 });
  const [buscando, setBuscando] = useState(() => !!initialRideId);
  const [dotCount, setDotCount] = useState(1);
  const [corridaConfirmada, setCorridaConfirmada] = useState(() => !!initialRideId);
  const [hasLocation, setHasLocation] = useState(false);
  const [cityBlocked, setCityBlocked] = useState(null);

  useEffect(() => {
    if (!openPainel) {
      setAddressSearchActive(false);
      return undefined;
    }

    const viewport = window.visualViewport;
    const updateViewport = () => {
      const next = getVisibleViewportMetrics(viewport, window.innerHeight);
      setAddressViewport((current) => (
        current.height === next.height
          && current.offsetTop === next.offsetTop
          && current.keyboardOpen === next.keyboardOpen
          ? current
          : next
      ));
    };

    updateViewport();
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);
    return () => {
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, [openPainel]);

  const [rideId, setRideId] = useState(() => initialRideId || null);
  const [corridaStatus, setCorridaStatus] = useState(null);
  const [dadosMotorista, setDadosMotorista] = useState(null);
  const [corridaCompletada, setCorridaCompletada] = useState(null);
  const [showRating, setShowRating] = useState(false);
  const unsubscribeRideRef = useRef(null);
  const watchIdRef = useRef(null);
  const posicaoAtualRef = useRef(null);
  const enderecoOrigemRef = useRef("Minha localização");
  const geocodeOrigemRef = useRef(null);
  const lastGeocodedPositionRef = useRef(null);
  const lastGeocodeAttemptRef = useRef(null);
  const geocodingOriginRef = useRef(false);
  const [enderecoGpsLabel, setEnderecoGpsLabel] = useState("");
  const [locationStatus, setLocationStatus] = useState("checking");
  const [, setLocationPermissionStatus] = useState("checking");
  const [, setLocationCoordinatesStatus] = useState("checking");
  const [, setGeocodingStatus] = useState("idle");
  const [cidadeDetectada, setCidadeDetectada] = useState("Jaraguá do Sul");
  const corridaStatusRef = useRef(null);
  const cardAuthorizationRef = useRef(new Set());

  // Pagamento
  const [showPayment, setShowPayment] = useState(false);
  const [destinoPendente, setDestinoPendente] = useState(null);
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState(null);

  // Modo escolher no mapa
  const [modoEscolherNoMapa, setModoEscolherNoMapa] = useState(false);
  const [enderecoPin, setEnderecoPin] = useState(null); // { nome, endereco } do pin atual
  const [geocodingPin, setGeocodingPin] = useState(false);
  const pinCenterRef = useRef(null);
  const geocodeTimerRef = useRef(null);
  const lastGeocodedCenterRef = useRef(null);
  const geocodeRequestRef = useRef(0);
  // destino pré-selecionado vindo do mapa (para preencher EscolhaDestino)
  const [destinoDoMapa, setDestinoDoMapa] = useState(null);

  // Chat
  const [showChat, setShowChat] = useState(false);
  const [msgNaoLida, setMsgNaoLida] = useState(false); // eslint-disable-line no-unused-vars

  // Notificações in-app
  const [notifInApp, setNotifInApp] = useState(null);
  const notifTimerRef = useRef(null);

  // Pedido de telefone
  const [showPedirTelefone, setShowPedirTelefone] = useState(false);
  const [telefoneInput, setTelefoneInput] = useState("");
  const [salvandoTel, setSalvandoTel] = useState(false);
  const [telefonePerfil, setTelefonePerfil] = useState(() => readAuthScopedClientValue(initialUid, "telefone") || "");
  const [telefoneErro, setTelefoneErro] = useState("");
  const [telefoneObrigatorioNoPedido, setTelefoneObrigatorioNoPedido] = useState(false);
  const pagamentoPendenteRef = useRef(null);
  const temTelefonePerfil = normalizeBrazilianPhone(telefonePerfil).valid;

  // Verificação de perfil
  const [showVerifModal, setShowVerifModal] = useState(false);

  // Agendamento
  const [showAgendamento, setShowAgendamento] = useState(false);
  const [showParaQuem, setShowParaQuem] = useState(false);
  const [dataAgendada, setDataAgendada] = useState("");
  const [horaAgendada, setHoraAgendada] = useState("");
  const [agendadoLabel, setAgendadoLabel] = useState("Ir agora");
  const [agendamentoAtivo, setAgendamentoAtivo] = useState(null);
  const [paraQuemLabel, setParaQuemLabel] = useState("Para mim");
  const [nomeOutraPessoa, setNomeOutraPessoa] = useState("");

  // Corridas agendadas
  const [corridasAgendadas, setCorridasAgendadas] = useState([]);
  const [showAgendadas, setShowAgendadas] = useState(false);
  const unsubAgendadasRef = useRef(null);

  // Divisão de corrida (fare split)
  const [showFareSplit, setShowFareSplit] = useState(false);
  const [splitPessoas, setSplitPessoas] = useState(2);

  // Rastreio do motorista
  const [kmParaMim, setKmParaMim] = useState(null);
  const [showModalChegou, setShowModalChegou] = useState(false);
  const [showModalPagamento, setShowModalPagamento] = useState(false);
  const [dadosPagamento, setDadosPagamento] = useState(null);
  const unsubMotoristaRef = useRef(null);

  // Modal Pix QR Code (botão manual durante corrida)
  const [showModalPix, setShowModalPix] = useState(false);

  // ── NOVO: posição em tempo real do motorista para o mapa ──
  const [motoristaPosicao, setMotoristaPosicao] = useState(null);
  const [routeEncodedPolyline, setRouteEncodedPolyline] = useState("");

  // ── NOVO: popup "pagar Pix agora ou ao final?"
  const [showPixAntecipado, setShowPixAntecipado] = useState(false);
  const pixAntecipadoMostradoRef = useRef(false); // garante exibir apenas 1x por corrida
  const pixAntecipadoTimerRef = useRef(null); // cleanup do setTimeout do PixAntecipado

  // ── Timeout automático: cancela se não achar motorista em 5 min
  const timeoutBuscaRef = useRef(null);
  // ── Modal de aviso ao cancelar corrida já aceita
  const [showAvisoCancelar, setShowAvisoCancelar] = useState(false);
  // ── ETA calculado em minutos (motorista → passageiro)
  const [etaMin, setEtaMin] = useState(null);
  // ── Progresso da corrida in_progress (0-95%)
  const [progressoCorrida, setProgressoCorrida] = useState(0);
  const corridaIniciadoEmRef = useRef(null);

  // ── Persiste rideId no localStorage para sobreviver a navegação entre abas
  useEffect(() => {
    if (rideId) {
      localStorage.setItem("activeRideId", rideId);
    } else {
      localStorage.removeItem("activeRideId");
    }
  }, [rideId]);

  // ── Valida activeRideId do localStorage ao iniciar — limpa se corrida não existe ou já terminou
  useEffect(() => {
    const savedId = localStorage.getItem("activeRideId");
    if (!savedId) return;
    import("../services/rideService").then(({ getRideById }) => {
      getRideById(savedId).then((corrida) => {
        const terminada = !corrida || corrida.status === "completed" || corrida.status === "cancelled";
        if (terminada) {
          localStorage.removeItem("activeRideId");
          setRideId(null);
          setBuscando(false);
          setCorridaConfirmada(false);
        }
      }).catch(() => {
        localStorage.removeItem("activeRideId");
        setRideId(null);
        setBuscando(false);
        setCorridaConfirmada(false);
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Timeout automático: cancela busca após 5 minutos sem motorista
  useEffect(() => {
    if (buscando && rideId) {
      timeoutBuscaRef.current = setTimeout(async () => {
        // Só cancela se ainda está aguardando (pending) — não cancela corrida já aceita
        if (corridaStatusRef.current && corridaStatusRef.current !== "pending") return;
        try { await cancelarCorrida(rideId); } catch (e) { console.error(e); }
        setBuscando(false); setRideId(null); setCorridaStatus(null); setRouteEncodedPolyline("");
        corridaStatusRef.current = null; setDestino(null);
        setCorridaConfirmada(false); setPagamentoSelecionado(null);
        pixAntecipadoMostradoRef.current = false;
        if (unsubscribeRideRef.current) { unsubscribeRideRef.current(); unsubscribeRideRef.current = null; }
        mostrarNotif("timeout", "Sem motoristas disponíveis", "Não encontramos ninguém próximo. Tente novamente.", "#f59e0b");
      }, 5 * 60 * 1000);
    } else {
      clearTimeout(timeoutBuscaRef.current);
    }
    return () => clearTimeout(timeoutBuscaRef.current);
  }, [buscando, rideId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ETA em minutos: calcula a partir da distância do motorista (~25 km/h em cidade)
  useEffect(() => {
    if (kmParaMim !== null && corridaStatus === "accepted") {
      const min = Math.ceil((kmParaMim / 25) * 60);
      setEtaMin(min < 1 ? 1 : min);
    } else {
      setEtaMin(null);
    }
  }, [kmParaMim, corridaStatus]);

  const mostrarNotif = useCallback((tipo, titulo, mensagem, cor) => {
    if (notifTimerRef.current) { clearTimeout(notifTimerRef.current); notifTimerRef.current = null; }
    setNotifInApp({ tipo, titulo, mensagem, cor: cor || "#00c896" });
    notifTimerRef.current = setTimeout(() => { setNotifInApp(null); notifTimerRef.current = null; }, 6000);
  }, []);

  useEffect(() => {
    return () => {
      if (notifTimerRef.current) { clearTimeout(notifTimerRef.current); notifTimerRef.current = null; }
      if (unsubMotoristaRef.current) { unsubMotoristaRef.current(); unsubMotoristaRef.current = null; }
      if (unsubscribeRideRef.current) { unsubscribeRideRef.current(); unsubscribeRideRef.current = null; }
      if (unsubAgendadasRef.current) { unsubAgendadasRef.current(); unsubAgendadasRef.current = null; }
      clearTimeout(timeoutBuscaRef.current);
    };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("deviceId")) {
      localStorage.setItem("deviceId", Math.random().toString(36).slice(2));
    }
  }, []);

  // Uma única fonte de verdade atende Web, iOS e Android sem repetir prompts.
  useEffect(() => {
    let active = true;
    const myDeviceId = localStorage.getItem("deviceId");
    const geocodeOrigem = async (lat, lng, { force = false } = {}) => {
      const nextPosition = { lat, lng };
      if (geocodingOriginRef.current) return null;
      const lastAttempt = lastGeocodeAttemptRef.current;
      if (!force && lastAttempt
        && distanceInMeters(lastAttempt.position, nextPosition) < 100
        && Date.now() - lastAttempt.at < 15000) return null;
      if (lastGeocodedPositionRef.current
        && distanceInMeters(lastGeocodedPositionRef.current, nextPosition) < 100
        && enderecoOrigemRef.current !== "Minha localização") return null;

      lastGeocodeAttemptRef.current = { position: nextPosition, at: Date.now() };
      geocodingOriginRef.current = true;
      setGeocodingStatus("loading");
      try {
        const address = await reverseGeocode(lat, lng);
        if (!address) { setGeocodingStatus("unavailable"); return null; }
        lastGeocodedPositionRef.current = nextPosition;
        enderecoOrigemRef.current = address.singleLine;
        setEnderecoGpsLabel(address.singleLine);
        if (address.city) setCidadeDetectada(address.city);
        setGeocodingStatus("ready");
        return address;
      } catch (cause) {
        setGeocodingStatus("unavailable");
        console.warn("[ZunoGeocoding]", { stage: "ORIGIN_REVERSE_GEOCODING_ERROR", code: cause?.code || "unknown", message: cause?.message || "unknown" });
        return null;
      } finally {
        geocodingOriginRef.current = false;
      }
    };
    geocodeOrigemRef.current = geocodeOrigem;

    const startLocation = async () => {
      let permission = await checkLocationPermission();
      if (!active) return;
      setLocationPermissionStatus(permission);
      persistLocationState(permission);
      if (permission === "prompt" || permission === "prompt-with-rationale") {
        const initialPosition = await getCurrentLocation({ requestPermission: true, maximumAge: 0, timeout: 15000 });
        if (!active) return;
        permission = "granted";
        setLocationPermissionStatus("granted");
        setLocationCoordinatesStatus("available");
        posicaoAtualRef.current = initialPosition;
        persistLocationState("granted", initialPosition);
        localStorage.setItem("locationDeviceId", myDeviceId);
        setHasLocation(true);
        setLocationStatus("ready");
        geocodeOrigem(initialPosition.lat, initialPosition.lng);
      }
      if (permission !== "granted") {
        setHasLocation(false);
        setLocationCoordinatesStatus("unavailable");
        setLocationStatus(permission);
        return;
      }
      watchIdRef.current = await watchLocation(
        (p) => {
          if (!active) return;
        posicaoAtualRef.current = p;
        persistLocationState("granted", p);
        localStorage.setItem("locationDeviceId", myDeviceId);
        setLocationPermissionStatus("granted");
        setLocationCoordinatesStatus("available");
        setHasLocation(true);
        setLocationStatus("ready");
        geocodeOrigem(p.lat, p.lng);
      },
      (error) => {
        if (!active) return;
        const savedDeviceId = localStorage.getItem("locationDeviceId");
        const lat = localStorage.getItem("userLat");
        const lng = localStorage.getItem("userLng");
        if (lat && lng && savedDeviceId === myDeviceId) {
          posicaoAtualRef.current = { lat: parseFloat(lat), lng: parseFloat(lng) };
          setLocationCoordinatesStatus("available");
          setHasLocation(true);
          setLocationStatus("ready");
          geocodeOrigem(posicaoAtualRef.current.lat, posicaoAtualRef.current.lng);
        } else {
          setLocationCoordinatesStatus("unavailable");
          setHasLocation(false);
          setLocationStatus(error?.reason || "unavailable");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    };
    startLocation().catch((cause) => {
      if (!active) return;
      console.warn("[ZunoLocation]", { stage: "HOME_WATCH_ERROR", code: cause?.code || "unknown", message: cause?.message || "unknown" });
      setLocationStatus(cause?.reason || "unavailable");
    });
    return () => {
      active = false;
      watchIdRef.current?.stop?.().catch(() => null);
      watchIdRef.current = null;
    };
  }, []);

  // Garante geocoding da origem (tenta após GPS disponível, com retry)
  useEffect(() => {
    let attempts = 0;
    const iv = setInterval(() => {
      const pos = posicaoAtualRef.current;
      if (!pos || geocodingOriginRef.current) return;
      if (enderecoOrigemRef.current !== "Minha localização") { clearInterval(iv); return; }
      attempts += 1;
      geocodeOrigemRef.current?.(pos.lat, pos.lng);
      if (attempts >= 4) clearInterval(iv);
    }, 3000);
    const timeout = setTimeout(() => clearInterval(iv), 13000);
    return () => { clearInterval(iv); clearTimeout(timeout); };
  }, []);

  const getLocation = useCallback(async () => {
    const myDeviceId = localStorage.getItem("deviceId");
    setLocationStatus("loading");
    try {
        const p = await getCurrentLocation({ requestPermission: true, maximumAge: 0, timeout: 10000 });
        posicaoAtualRef.current = p;
        persistLocationState("granted", p);
        localStorage.setItem("zunoLocationPrimer", "accepted");
        localStorage.setItem("locationDeviceId", myDeviceId);
        setLocationPermissionStatus("granted");
        setLocationCoordinatesStatus("available");
        setHasLocation(true);
        setLocationStatus("ready");
        return p;
    } catch (cause) {
      setLocationPermissionStatus(cause?.reason === "denied" ? "denied" : "unknown");
      setLocationCoordinatesStatus("unavailable");
      persistLocationState(cause?.reason || "unavailable");
      setLocationStatus(cause?.reason || "unavailable");
      return null;
    }
  }, []);

  const retryCurrentLocation = useCallback(async () => {
    const position = await getLocation();
    if (position) await geocodeOrigemRef.current?.(position.lat, position.lng, { force: true });
  }, [getLocation]);

  // Auth
  useEffect(() => {
    if (isStoreScreenshotCapture) {
      setUserUid(storeScreenshotUser.uid);
      setUsuario("Passageiro");
      return undefined;
    }
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate("/login", { replace: true }); return; }
      bindClientStorageToUser(user.uid);
      const requestedUid = user.uid;
      setUserUid(user.uid);
      setUsuario("");
      setFotoUsuario("");
      setTelefonePerfil("");
      const fotoSalva = getCachedProfilePhoto();
      setFotoUsuario(fotoSalva || user.photoURL || "");
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (auth.currentUser?.uid !== requestedUid) return;
        if (snap.exists()) {
          const data = snap.data();
          if (data.nome) { setUsuario(data.nome.split(" ")[0]); localStorage.setItem("usuarioLogado", data.nome); }
          if (data.photoURL) {
            setFotoUsuario(data.photoURL);
            setCachedProfilePhoto(data.photoURL);
          } else if (!fotoSalva && user.photoURL) {
            setFotoUsuario(user.photoURL);
            setCachedProfilePhoto(user.photoURL);
          }
          const telefoneEncontrado = data.telefone || user.phoneNumber || "";
          const telefoneNormalizado = normalizeBrazilianPhone(telefoneEncontrado);
          if (telefoneNormalizado.valid) {
            setTelefonePerfil(telefoneNormalizado.national);
            localStorage.setItem("telefone", telefoneNormalizado.national);
            setShowPedirTelefone(false);
          } else if (user.providerData?.some(p => p.providerId === "google.com")) {
            setTelefoneObrigatorioNoPedido(false);
            setShowPedirTelefone(true);
          }
        } else {
          if (user.displayName) setUsuario(user.displayName.split(" ")[0]);
          else if (user.email) setUsuario(user.email.split("@")[0]);
          const telefoneNormalizado = normalizeBrazilianPhone(user.phoneNumber || "");
          if (telefoneNormalizado.valid) {
            setTelefonePerfil(telefoneNormalizado.national);
            localStorage.setItem("telefone", telefoneNormalizado.national);
          } else if (user.providerData?.some(p => p.providerId === "google.com")) {
            setTelefoneObrigatorioNoPedido(false);
            setShowPedirTelefone(true);
          }
        }
      } catch (e) {
        if (auth.currentUser?.uid !== requestedUid) return;
        const neutralName = user.displayName || user.email?.split("@")[0] || "";
        setUsuario(neutralName ? neutralName.split(" ")[0] : "");
      }
      if (localStorage.getItem("zunoNotificationPrimer") === "accepted") {
        inicializarNotificacoes("passenger").catch(() => {});
      }
      if (unsubAgendadasRef.current) unsubAgendadasRef.current();
      unsubAgendadasRef.current = escutarCorridasAgendadas(user.uid, (lista) => { setCorridasAgendadas(lista); });
    });
    return () => { unsub(); if (unsubAgendadasRef.current) unsubAgendadasRef.current(); };
  }, [navigate]);

  useEffect(() => subscribeToProfilePhoto(setFotoUsuario), []);

  const salvarTelefone = async () => {
    const normalized = normalizeBrazilianPhone(telefoneInput);
    setTelefoneErro("");
    if (!normalized.valid) {
      setTelefoneErro("Informe um telefone válido com DDD.");
      return;
    }
    setSalvandoTel(true);
    try {
      const user = auth.currentUser;
      console.info("[RidePhone] Iniciando persistência", { digits: normalized.national.length, suffix: normalized.national.slice(-4), hasPendingRide: !!pagamentoPendenteRef.current });
      const pagamentoPendente = pagamentoPendenteRef.current;
      await persistPhoneAndResumeRide({
        userId: user?.uid,
        rawPhone: telefoneInput,
        pendingPayment: pagamentoPendente,
        onPhoneSaved: async saved => {
          localStorage.setItem("telefone", saved.national);
          setTelefonePerfil(saved.national);
          pagamentoPendenteRef.current = null;
          setShowPedirTelefone(false);
          setTelefoneObrigatorioNoPedido(false);
          console.info("[RidePhone] Telefone persistido e perfil local atualizado", { hasPendingRide: !!pagamentoPendente });
        },
        resumeRide: async pagamento => {
          console.info("[RidePhone] Retomando solicitação de corrida pendente");
          await continuarPedidoCorrida(pagamento);
        },
      });
    } catch (e) {
      console.error("[RidePhone] Falha ao salvar telefone", { code: e?.code || "profile/unknown", message: e?.message || "Erro desconhecido" });
      setTelefoneErro(e?.code === "auth/user-not-authenticated"
        ? "Sua sessão expirou. Entre novamente para continuar."
        : "Não foi possível salvar o telefone. Verifique sua conexão e tente novamente.");
    } finally {
      setSalvandoTel(false);
    }
  };

  const fmtTel = (v) => {
    v = v.replace(/\D/g,"").slice(0,11);
    if(v.length > 6) return v.replace(/^(\d{2})(\d{5})(\d+)/,"($1) $2-$3");
    if(v.length > 2) return v.replace(/^(\d{2})(\d+)/,"($1) $2");
    return v;
  };

  // Notificações foreground
  useEffect(() => {
    const unsub = escutarNotificacoesForeground((notif) => {
      if (notif?.data?.tipo === "motorista_aceito") mostrarNotif("aceito","Motorista encontrado",notif.body,"#00c896");
      else if (notif?.data?.tipo === "corrida_concluida") mostrarNotif("concluida","Corrida finalizada",notif.body,"#22c55e");
    });
    return () => { if (typeof unsub === "function") unsub(); };
  }, [mostrarNotif]);

  // Dots animados no "buscando"
  useEffect(() => {
    if (!buscando) return;
    const iv = setInterval(() => setDotCount(d => d >= 3 ? 1 : d + 1), 500);
    return () => clearInterval(iv);
  }, [buscando]);

  // ── rastreia posição do motorista em tempo real via RTDB (WebSocket, sem custo por leitura)
  useEffect(() => {
    // Limpa listener anterior
    if (unsubMotoristaRef.current) {
      unsubMotoristaRef.current();
      unsubMotoristaRef.current = null;
    }
    if (!dadosMotorista?.id) {
      setKmParaMim(null);
      setMotoristaPosicao(null);
      return;
    }
    const locationRef = rtdbRef(rtdb, `drivers_location/${dadosMotorista.id}`);
    const unsub = onValue(locationRef, (snap) => {
      const d = snap.val();
      if (!d?.lat || !d?.lng) return;
      setMotoristaPosicao({ lat: d.lat, lng: d.lng });
      if (posicaoAtualRef.current) {
        const R = 6371;
        const dLat = (d.lat - posicaoAtualRef.current.lat) * Math.PI / 180;
        const dLng = (d.lng - posicaoAtualRef.current.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2)**2 + Math.cos(posicaoAtualRef.current.lat*Math.PI/180)*Math.cos(d.lat*Math.PI/180)*Math.sin(dLng/2)**2;
        setKmParaMim(+(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))).toFixed(1));
      }
    });
    unsubMotoristaRef.current = unsub;
    return () => { if (unsubMotoristaRef.current) { unsubMotoristaRef.current(); unsubMotoristaRef.current = null; } };
  }, [dadosMotorista?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escuta status da corrida
  useEffect(() => {
    if (unsubscribeRideRef.current) { unsubscribeRideRef.current(); unsubscribeRideRef.current = null; }
    if (!rideId) return;
    unsubscribeRideRef.current = escutarCorrida(rideId, (corrida) => {
      const prevStatus = corridaStatusRef.current;
      corridaStatusRef.current = corrida.status;
      setCorridaStatus(corrida.status);
      setRouteEncodedPolyline(corrida.routeEncodedPolyline || "");

      if (["accepted", "arrived", "in_progress", "completion_pending"].includes(corrida.status)) {
        if (corrida.status === "accepted" && corrida.metodoPagamento === "cartao_zuno" && corrida.paymentCardId && !cardAuthorizationRef.current.has(corrida.id)) {
          cardAuthorizationRef.current.add(corrida.id);
          authorizeZunoCardRide(corrida.id, corrida.paymentCardId).catch((error) => {
            cardAuthorizationRef.current.delete(corrida.id);
            console.error("Falha na pré-autorização:", error?.code || error?.message);
            mostrarNotif("pagamento", "Cartão não autorizado", "Escolha outro meio de pagamento.", "#ef4444");
          });
        }
        const isNovo = prevStatus === "pending" && corrida.status === "accepted";
        setDadosMotorista({
          nome: corrida.motoristaNome,
          foto: corrida.motoristaFoto,
          marca: corrida.motoristaMarca,
          modelo: corrida.motoristaModelo,
          placa: corrida.motoristaPlaca,
          cor: corrida.motoristaCor || "",
          tipoVeiculo: corrida.motoristaTipoVeiculo || "carro",
          avaliacao: corrida.motoristaAvaliacao,
          id: corrida.motoristaid,
          pixMotorista: corrida.pixMotorista,
          qrCodePix: corrida.qrCodePix || null,
           metodoPagamento: corrida.metodoPagamento || "dinheiro",
        });
        setBuscando(false);
        if (isNovo) mostrarNotif("aceito", "Motorista encontrado", `${corrida.motoristaNome} está a caminho!`, "#00c896");
      }

      if (corrida.status === "arrived" && prevStatus === "accepted") {
        setShowModalChegou(true);
        mostrarNotif("chegou", "Motorista chegou", "Seu motorista está te esperando agora.", "#f59e0b");
      }

      // ── NOVO: ao iniciar corrida, se pagamento for Pix, mostra popup antecipado
      if (corrida.status === "in_progress" && (prevStatus === "accepted" || prevStatus === "arrived")) {
        setShowModalChegou(false);
        mostrarNotif("iniciou", "Corrida iniciada", "Boa viagem! Você está em rota.", "#06b6d4");
        // Marca o início para calcular progresso temporal
        corridaIniciadoEmRef.current = corrida.iniciadoEm?.toDate?.() || new Date();

        const isPix = corrida.metodoPagamento === "pix";
        const pixJaPago = corrida.pixPago === true;
        if (isPix && !pixJaPago && !pixAntecipadoMostradoRef.current) {
          pixAntecipadoMostradoRef.current = true;
          // Pequeno delay para não sobrepor a notificação de início
          clearTimeout(pixAntecipadoTimerRef.current);
          pixAntecipadoTimerRef.current = setTimeout(() => setShowPixAntecipado(true), 1200);
        }
      }

      if (corrida.status === "completed") {
        const valor = corrida.valorFinalComTaxa || corrida.valor;
        const metPag = corrida.metodoPagamento === "pix" ? "Pix"
          : corrida.metodoPagamento === "maquininha" ? "Cartão na maquininha"
            : corrida.metodoPagamento === "cartao_zuno" ? "Cartão" : "Dinheiro";

        // Captura TODOS os dados necessários para o modal ANTES de limpar o estado
        // (evita race condition onde setDadosMotorista(null) executa antes do modal renderizar)
        const dadosPag = {
          valor,
          metodo: corrida.metodoPagamento,
          labelMetodo: metPag,
          pixMotorista: corrida.pixMotorista,
          qrCodePix: corrida.qrCodePix || null,
          motoristaNome: corrida.motoristaNome,
          destino: corrida.destino?.nome || "Destino",
          pixJaPago: corrida.pixPago === true,
        };
        const corridaComp = {
          rideId: corrida.id,
          motoristaId: corrida.motoristaid,
          motoristaNome: corrida.motoristaNome,
          motoristaFoto: corrida.motoristaFoto,
        };

        clearTimeout(pixAntecipadoTimerRef.current);
        pixAntecipadoMostradoRef.current = false;
        corridaStatusRef.current = null;

        // Limpa estado da corrida e mostra modal em um único batch de re-render
        setBuscando(false);
        setRideId(null);
        setCorridaStatus(null);
        setRouteEncodedPolyline("");
        setDadosMotorista(null);
        setDestino(null);
        setCorridaConfirmada(false);
        setPagamentoSelecionado(null);
        setShowModalPix(false);
        setShowPixAntecipado(false);
        setCorridaCompletada(corridaComp);
        setDadosPagamento(dadosPag);
        setShowModalPagamento(true);
      }

      if (corrida.status === "cancelled") {
        if (prevStatus === "accepted" || prevStatus === "arrived" || prevStatus === "in_progress") {
          const canceladoPorMotorista = corrida.canceladoPor === "motorista" || (!corrida.canceladoPor && corrida.motoristaid);
          const msgCancel = canceladoPorMotorista ? "O motorista cancelou a corrida." : "Corrida cancelada com sucesso.";
          mostrarNotif("cancelada", "Corrida cancelada", msgCancel, "#f87171");
        }
        clearTimeout(pixAntecipadoTimerRef.current);
        setBuscando(false); setRideId(null); setCorridaStatus(null); setRouteEncodedPolyline(""); corridaStatusRef.current = null;
        setDadosMotorista(null); setDestino(null); setCorridaConfirmada(false); setPagamentoSelecionado(null);
        setShowPixAntecipado(false);
        pixAntecipadoMostradoRef.current = false;
      }
    });
    return () => { if (unsubscribeRideRef.current) { unsubscribeRideRef.current(); unsubscribeRideRef.current = null; } };
  }, [rideId, mostrarNotif]);

  // ── Progresso temporal da corrida in_progress ─────────────────
  useEffect(() => {
    if (corridaStatus !== "in_progress") { setProgressoCorrida(0); return; }
    const tempoEstimado = (destino?.dadosCorrida?.tempoMin || dadosRota?.min || 15) * 60 * 1000; // ms
    const calcular = () => {
      if (!corridaIniciadoEmRef.current) return;
      const decorrido = Date.now() - corridaIniciadoEmRef.current.getTime();
      setProgressoCorrida(Math.min(95, Math.round((decorrido / tempoEstimado) * 100)));
    };
    calcular();
    const iv = setInterval(calcular, 10000);
    return () => clearInterval(iv);
  }, [corridaStatus, destino, dadosRota]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRouteCalculated = useCallback((km, min) => setDadosRota({ km, min }), []);
  const handleDestinoConfirmado = (d) => { setDestinoPendente(d); setOpenPainel(false); setShowPayment(true); };

  const handleAbrirEscolherNoMapa = () => {
    setOpenPainel(false);
    setEnderecoPin(null);
    setGeocodingPin(false);
    lastGeocodedCenterRef.current = null;
    geocodeRequestRef.current += 1;
    setModoEscolherNoMapa(true);
    if (posicaoAtualRef.current) {
      pinCenterRef.current = { lat: posicaoAtualRef.current.lat, lng: posicaoAtualRef.current.lng };
    }
  };

  // Atualiza o endereço sob o pino após uma pausa curta no gesto, sem bloquear o mapa.
  const handlePinCenterChange = useCallback((lat, lng) => {
    const center = { lat, lng };
    pinCenterRef.current = center;
    clearTimeout(geocodeTimerRef.current);

    if (distanceInMeters(lastGeocodedCenterRef.current, center) < 18) {
      setGeocodingPin(false);
      return;
    }

    setGeocodingPin(true);
    const requestId = ++geocodeRequestRef.current;
    geocodeTimerRef.current = setTimeout(async () => {
      const address = await reverseGeocode(lat, lng);
      if (requestId !== geocodeRequestRef.current) return;
      const latestCenter = pinCenterRef.current;
      if (distanceInMeters(center, latestCenter) > 25) return;
      lastGeocodedCenterRef.current = center;
      setEnderecoPin({
        nome: address?.primary || "Local selecionado",
        endereco: address?.singleLine || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        lat,
        lng,
      });
      setGeocodingPin(false);
    }, 450);
  }, []);

  useEffect(() => () => {
    clearTimeout(geocodeTimerRef.current);
    geocodeRequestRef.current += 1;
  }, []);

  const handleConfirmarPinNoMapa = async () => {
    const pos = pinCenterRef.current;
    if (!Number.isFinite(pos?.lat) || !Number.isFinite(pos?.lng)) return;
    clearTimeout(geocodeTimerRef.current);

    let currentAddress = enderecoPin;
    const addressMatchesPin = currentAddress
      && distanceInMeters(currentAddress, pos) <= 25;
    if (!addressMatchesPin) {
      const requestId = ++geocodeRequestRef.current;
      setGeocodingPin(true);
      const address = await reverseGeocode(pos.lat, pos.lng);
      if (requestId !== geocodeRequestRef.current) return;
      currentAddress = {
        nome: address?.primary || "Local selecionado",
        endereco: address?.singleLine || `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`,
        lat: pos.lat,
        lng: pos.lng,
      };
      setEnderecoPin(currentAddress);
      setGeocodingPin(false);
    }

    const nome = currentAddress?.nome || "Local no mapa";
    const endereco = currentAddress?.endereco || `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`;
    // Fecha o modo mapa e abre o painel com o destino pré-selecionado
    // para calcular rota real + preço antes de ir para pagamento
    setModoEscolherNoMapa(false);
    setDestinoDoMapa({ lat: pos.lat, lng: pos.lng, nome, endereco });
    setOpenPainel(true);
  };

  const continuarPedidoCorrida = async (pagamento) => {
    setPagamentoSelecionado(pagamento);
    setShowPayment(false);
    const nd = destinoPendente;
    if (!nd?.dadosCorrida) return;
    if (!hasLocation || !posicaoAtualRef.current) {
      const pos = await getLocation();
      if (!pos) { alert("Para solicitar uma corrida, precisamos da sua localização."); return; }
    }
    const hist = JSON.parse(localStorage.getItem("corridasHistorico") || "[]");
    localStorage.setItem("corridasHistorico", JSON.stringify([{
      ...nd.dadosCorrida, tipo: nd.categoria, destino: nd.name||"Destino",
      lat: nd.lat, lng: nd.lng, address: nd.address||"",
      distanciaKm: dadosRota.km, tempoMin: dadosRota.min,
      data: new Date().toLocaleDateString("pt-BR",{day:"2-digit",month:"short",year:"numeric"}),
    }, ...hist].slice(0,10)));
    pixAntecipadoMostradoRef.current = false;
    setDestino(nd); setCorridaConfirmada(true); setBuscando(true);
    try {
      const user = auth.currentUser;
      const id = await criarCorrida({
        passageiroId: user?.uid || userUid || "anon",
        passageiroNome: usuario || localStorage.getItem("usuarioLogado") || "Passageiro",
        passageiroFoto: fotoUsuario || user?.photoURL || "",
        origem: { lat: posicaoAtualRef.current.lat, lng: posicaoAtualRef.current.lng, endereco: enderecoOrigemRef.current },
        destino: { lat: nd.lat, lng: nd.lng, nome: nd.name||"Destino", endereco: nd.address||"" },
        categoria: nd.categoria,
        // O valor-base da corrida pertence integralmente ao motorista.
        // A obrigação Flex é registrada separadamente ao concluir a corrida.
        dadosCorrida: { ...nd.dadosCorrida, distanciaKm: dadosRota.km || nd.dadosCorrida?.distanciaKm || 0, tempoMin: dadosRota.min || nd.dadosCorrida?.tempoMin || 0, driverEarns: nd.dadosCorrida.finalPrice },
        pagamento,
        agendamento: agendamentoAtivo,
      });
      if (agendamentoAtivo) {
        setBuscando(false); setCorridaConfirmada(false); setDestino(null); setPagamentoSelecionado(null);
        setAgendamentoAtivo(null); setAgendadoLabel("Ir agora");
        mostrarNotif("agendado", "Corrida agendada", `Para ${agendamentoAtivo.hora} de ${formatarDataBR(agendamentoAtivo.data)}`, "#a855f7");
        setShowAgendadas(true);
      } else { setRideId(id); }
    } catch (err) {
      console.error(err);
      setBuscando(false);
      setCorridaConfirmada(false);
      setDestino(null);
      if (err?.details?.reason === "CITY_NOT_OPERATIONAL") {
        setCityBlocked(err.details);
      } else if (err?.details?.reason === "LEGAL_ACCEPTANCE_REQUIRED") {
        mostrarNotif("termos", "Documentos atualizados", "Reabra a tela inicial para revisar os documentos atuais.", "#f59e0b");
      } else {
        mostrarNotif("erro", "Erro ao solicitar", "Tente novamente em instantes.", "#f87171");
      }
    }
  };

  const handlePagamentoConfirmado = async (pagamento) => {
    // A foto continua sendo um requisito de segurança já existente.
    if (!fotoUsuario) {
      setShowVerifModal(true);
      return;
    }

    if (!temTelefonePerfil) {
      pagamentoPendenteRef.current = pagamento;
      setTelefoneObrigatorioNoPedido(true);
      setTelefoneErro("");
      setTelefoneInput(telefonePerfil ? fmtTel(telefonePerfil) : "");
      setShowPayment(false);
      setShowPedirTelefone(true);
      console.info("[RidePhone] Solicitação pausada aguardando telefone", { hasPendingRide: true });
      return;
    }

    await continuarPedidoCorrida(pagamento);
  };

  const executarCancelamento = async (motivo = "") => {
    clearTimeout(timeoutBuscaRef.current);
    if (rideId) {
      try {
        if (motivo) await cancelarCorridaComMotivo(rideId, motivo);
        else await cancelarCorrida(rideId);
        mostrarNotif("cancelada", "Corrida cancelada", "Sua corrida foi cancelada com sucesso.", "#f87171");
      } catch (e) { console.error(e); }
    }
    setBuscando(false); setRideId(null); setCorridaStatus(null); setRouteEncodedPolyline(""); corridaStatusRef.current = null;
    setDadosMotorista(null); setDestino(null); setCorridaConfirmada(false); setPagamentoSelecionado(null);
    setShowPixAntecipado(false); setShowAvisoCancelar(false);
    pixAntecipadoMostradoRef.current = false;
    if (unsubscribeRideRef.current) { unsubscribeRideRef.current(); unsubscribeRideRef.current = null; }
  };

  const handleCancelar = async () => {
    // Se motorista já aceitou, avisar antes de cancelar
    if (corridaStatus === "accepted" || corridaStatus === "arrived") {
      setShowAvisoCancelar(true);
      return;
    }
    await executarCancelamento();
  };

  const handleCancelarAgendada = async (item) => {
    try {
      await cancelarCorridaAgendada(item.id, item.rideId);
      mostrarNotif("cancelada", "Agendamento cancelado", "Sua corrida agendada foi cancelada.", "#f87171");
    } catch (e) { console.error(e); }
  };

  const handleSubmitRating = async ({ estrelas, elogios, comentario }) => {
    if (!corridaCompletada) return;
    await avaliarMotorista({ rideId: corridaCompletada.rideId, motoristaId: corridaCompletada.motoristaId, estrelas, elogios, comentario });
  };

  const confirmarAgendamento = () => {
    if (!dataAgendada || !horaAgendada) return;
    const d = new Date(`${dataAgendada}T${horaAgendada}`);
    if (d <= new Date()) { alert("Escolha um horário no futuro."); return; }
    setAgendamentoAtivo({ data: dataAgendada, hora: horaAgendada });
    setAgendadoLabel(`${formatarDataBR(dataAgendada)} ${horaAgendada}`);
    setShowAgendamento(false);
  };

  const cancelarAgendamento = () => {
    setAgendamentoAtivo(null); setDataAgendada(""); setHoraAgendada("");
    setAgendadoLabel("Ir agora"); setShowAgendamento(false);
  };

  // Abrir/copiar o Pix não comprova pagamento. Somente motorista/backend
  // pode confirmar recebimento; nunca marcamos pixPago ao fechar o QR.
  const handlePixAberto = () => {
    setShowPixAntecipado(false);
    mostrarNotif("pix", "Pix aberto", "Conclua o pagamento no aplicativo do seu banco.", "#06b6d4");
  };

  // ── NOVO: passageiro escolheu pagar ao final
  const handlePixPagarFinal = () => {
    setShowPixAntecipado(false);
  };

  const handleCompartilharViagem = async () => {
    if (!rideId) return;
    try {
      const user = auth.currentUser;
      const nome = usuario || localStorage.getItem("usuarioLogado") || "Passageiro";
      const url = await criarShareLink({ rideId, passageiroId: user?.uid, passageiroNome: nome });
      if (navigator.share) {
        await navigator.share({ title: "Acompanhe minha viagem — Zuno", text: `Estou em uma corrida! Acompanhe em tempo real:`, url });
      } else {
        await navigator.clipboard.writeText(url);
        mostrarNotif("share", "Link copiado", "Compartilhe o link da sua viagem com quem quiser.", "#a855f7");
      }
    } catch (e) { console.error(e); }
  };

  function saudacao() { const h = new Date().getHours(); return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite"; }
  function iconePag(m) { return m === "pix" ? "Pix" : m === "maquininha" ? "Maquininha" : m === "cartao_zuno" ? "Cartão" : "Dinheiro"; }
  function formatarDataBR(dateStr) {
    if (!dateStr) return "";
    const [, mes, dia] = dateStr.split("-");
    return `${dia}/${mes}`;
  }
  function formatarDataHoraBR(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) + " às " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  const dots = ".".repeat(dotCount);
  const valorExibido = destinoPendente?.dadosCorrida?.finalPrice || destino?.dadosCorrida?.finalPrice || 0;
  const isIdleHome = !openPainel && !buscando && !dadosMotorista;
  const primeiroNome = (usuario || "Passageiro").trim().split(/\s+/)[0];
  const origemExibida = enderecoGpsLabel || "R. Martim Francisco, 120";
  const lightReferenceHome = theme === "light";
  const homeCampaigns = getIOSHomeCampaigns(theme);

  useEffect(() => {
    if (!isIdleHome) return undefined;
    const resetHomeScroll = () => {
      const screen = homeScreenRef.current;
      if (screen && screen.scrollTop !== 0) screen.scrollTo({ top: 0, behavior: "auto" });
    };
    const frame = requestAnimationFrame(resetHomeScroll);
    window.addEventListener("pageshow", resetHomeScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pageshow", resetHomeScroll);
    };
  }, [isIdleHome]);

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <div ref={homeScreenRef} data-reference-ui="true" className={`zuno-app-shell passenger-home-screen${isIdleHome ? " passenger-home-screen--idle" : ""}`} style={{ position:"relative", width:"100%", height:"var(--zuno-home-viewport-height, 100dvh)", overflow:"hidden" }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideUp{from{opacity:0;transform:translateY(100%)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}
        @keyframes radar{0%{transform:scale(1);opacity:0.6}100%{transform:scale(2.2);opacity:0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        *{box-sizing:border-box} ::-webkit-scrollbar{width:0}
      `}</style>

      {showSessionIntro && <WelcomeScreen
        nome={usuario}
        returning={Boolean(userUid && localStorage.getItem(`zunoPassengerSeen:${userUid}`))}
        onContinue={() => {
          if (userUid) localStorage.setItem(`zunoPassengerSeen:${userUid}`, "1");
          onSessionIntroContinue?.();
        }}
      />}

      {/* Notificação in-app */}
      {notifInApp && (
        <div onClick={() => setNotifInApp(null)} style={{ position:"fixed",top:16,left:"50%",transform:"translateX(-50%)",zIndex:99999,background:"rgba(0,0,0,0.88)",backdropFilter:"blur(12px)",borderRadius:50,padding:"10px 20px",display:"flex",alignItems:"center",gap:10,boxShadow:`0 4px 20px ${notifInApp.cor}44`,border:`1px solid ${notifInApp.cor}44`,animation:"fadeUp 0.3s ease",cursor:"pointer",maxWidth:"92vw",whiteSpace:"nowrap" }}>
          <div style={{ width:8,height:8,borderRadius:"50%",background:notifInApp.cor,flexShrink:0 }} />
          <div>
            <p style={{ color:"#fff",fontWeight:700,fontSize:13,margin:0 }}>{notifInApp.titulo}</p>
            {notifInApp.mensagem && <p style={{ color:"rgba(255,255,255,0.55)",fontSize:11,margin:0 }}>{notifInApp.mensagem}</p>}
          </div>
        </div>
      )}

      {/* Modal verificação de perfil obrigatória */}
      {showVerifModal && (
        <div style={{ position:"fixed",inset:0,zIndex:99999,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 20px" }}>
          <div style={{ width:"100%",maxWidth:420,background:"#0d1527",borderRadius:24,padding:"32px 24px",border:"1px solid rgba(168,85,247,0.25)",animation:"slideUp 0.3s ease",textAlign:"center" }}>
            <div style={{ width:72,height:72,borderRadius:"50%",background:"linear-gradient(135deg,rgba(124,58,237,0.2),rgba(168,85,247,0.1))",border:"2px solid rgba(168,85,247,0.4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 20px" }}><LuShieldCheck /></div>
            <p style={{ color:"#fff",fontWeight:800,fontSize:20,margin:"0 0 10px" }}>Verifique seu perfil</p>
            <p style={{ color:"rgba(255,255,255,0.5)",fontSize:14,lineHeight:1.6,margin:"0 0 24px" }}>
              Para garantir a segurança de todos, é obrigatório ter <strong style={{ color:"rgba(255,255,255,0.8)" }}>foto de perfil</strong> e <strong style={{ color:"rgba(255,255,255,0.8)" }}>telefone</strong> cadastrados antes de solicitar uma corrida.
            </p>
            {/* Checklist de pendências */}
            <div style={{ background:"rgba(255,255,255,0.04)",borderRadius:14,padding:"14px 16px",marginBottom:24,textAlign:"left" }}>
              {[
                { ok: !!fotoUsuario, label: "Foto de perfil" },
                { ok: temTelefonePerfil, label: "Número de telefone" },
              ].map((item, i) => (
                <div key={i} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:i===0?"1px solid rgba(255,255,255,0.05)":"none" }}>
                  <div style={{ width:22,height:22,borderRadius:"50%",background:item.ok?"rgba(34,197,94,0.15)":"rgba(239,68,68,0.15)",border:`1px solid ${item.ok?"rgba(34,197,94,0.4)":"rgba(239,68,68,0.4)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    {item.ok
                      ? <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#22c55e" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      : <span style={{ color:"#ef4444",fontSize:12,fontWeight:800 }}>!</span>
                    }
                  </div>
                  <span style={{ fontSize:14,color:item.ok?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.9)",fontWeight:item.ok?400:600 }}>{item.label}</span>
                  {!item.ok && <span style={{ marginLeft:"auto",fontSize:11,color:"#ef4444",fontWeight:700 }}>Pendente</span>}
                  {item.ok && <span style={{ marginLeft:"auto",fontSize:11,color:"#22c55e",fontWeight:600 }}>OK</span>}
                </div>
              ))}
            </div>
            <button onClick={() => { setShowVerifModal(false); navigate("/conta"); }}
              style={{ width:"100%",padding:"15px",borderRadius:14,border:"none",background:"linear-gradient(135deg,#7c3aed,#a855f7)",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",marginBottom:10,fontFamily:"inherit" }}>
              Completar meu perfil →
            </button>
            <button onClick={() => setShowVerifModal(false)}
              style={{ width:"100%",padding:"12px",borderRadius:14,border:"1px solid rgba(255,255,255,0.1)",background:"transparent",color:"rgba(255,255,255,0.4)",fontSize:14,cursor:"pointer",fontFamily:"inherit" }}>
              Agora não
            </button>
          </div>
        </div>
      )}

      {/* Modal pedir telefone */}
      {showPedirTelefone && (
        <div style={{ position:"fixed",inset:0,zIndex:99998,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-end",justifyContent:"center" }}>
          <div style={{ width:"100%",maxWidth:480,background:"#0d1527",borderRadius:"24px 24px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,0.08)",borderBottom:"none",animation:"slideUp 0.3s ease" }}>
            <div style={{ width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 20px" }} />
            <p style={{ color:"#fff",fontWeight:800,fontSize:18,margin:"0 0 6px" }}>Confirme seu telefone</p>
            <p style={{ color:"rgba(255,255,255,0.4)",fontSize:13,margin:"0 0 20px" }}>
              {telefoneObrigatorioNoPedido
                ? "Precisamos do seu telefone para continuar a solicitação da corrida."
                : "Para receber atualizações da corrida por SMS"}
            </p>
            <input value={telefoneInput} onChange={e=>{setTelefoneInput(fmtTel(e.target.value));if(telefoneErro)setTelefoneErro("");}} placeholder="(11) 99999-9999" inputMode="tel" disabled={salvandoTel} style={{ width:"100%",padding:"14px 16px",borderRadius:12,border:`1px solid ${telefoneErro?"rgba(248,113,113,0.7)":"rgba(255,255,255,0.12)"}`,background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:16,marginBottom:telefoneErro?8:16,outline:"none",fontFamily:"inherit" }} />
            {telefoneErro && <p role="alert" style={{ color:"#f87171",fontSize:12,margin:"0 0 14px" }}>{telefoneErro}</p>}
            <button onClick={salvarTelefone} disabled={salvandoTel} style={{ width:"100%",padding:"14px",borderRadius:12,border:"none",background:"linear-gradient(135deg,#7c3aed,#a855f7)",color:"#fff",fontSize:15,fontWeight:800,cursor:salvandoTel?"wait":"pointer",fontFamily:"inherit",marginBottom:10,opacity:salvandoTel?0.7:1 }}>
              {salvandoTel ? "Salvando..." : "Confirmar"}
            </button>
            {!telefoneObrigatorioNoPedido && (
              <button onClick={()=>{pagamentoPendenteRef.current=null;setTelefoneErro("");setShowPedirTelefone(false);}} style={{ width:"100%",padding:"12px",borderRadius:12,border:"1px solid rgba(255,255,255,0.1)",background:"transparent",color:"rgba(255,255,255,0.4)",fontSize:14,cursor:"pointer",fontFamily:"inherit" }}>
                Pular por agora
              </button>
            )}
          </div>
        </div>
      )}

      {/* Painel agendamento */}
      {showAgendamento && (
        <div onClick={()=>setShowAgendamento(false)} style={{ position:"fixed",inset:0,zIndex:19999,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"flex-end",justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%",maxWidth:480,background:"#0d1527",borderRadius:"24px 24px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,0.08)",borderBottom:"none",animation:"slideUp 0.3s ease" }}>
            <div style={{ width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 20px" }} />
            <p style={{ color:"#fff",fontWeight:800,fontSize:18,margin:"0 0 20px" }}>Agendar corrida</p>
            <label style={{ color:"rgba(255,255,255,0.5)",fontSize:12,display:"block",marginBottom:6 }}>Data</label>
            <input type="date" value={dataAgendada} onChange={e=>setDataAgendada(e.target.value)} min={new Date().toISOString().slice(0,10)} style={{ width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:15,marginBottom:16,outline:"none",fontFamily:"inherit",colorScheme:"dark" }} />
            <label style={{ color:"rgba(255,255,255,0.5)",fontSize:12,display:"block",marginBottom:6 }}>Horário</label>
            <input type="time" value={horaAgendada} onChange={e=>setHoraAgendada(e.target.value)} style={{ width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,0.12)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:15,marginBottom:20,outline:"none",fontFamily:"inherit",colorScheme:"dark" }} />
            <button onClick={confirmarAgendamento} style={{ width:"100%",padding:"14px",borderRadius:12,border:"none",background:"linear-gradient(135deg,#7c3aed,#a855f7)",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:10 }}>Confirmar agendamento</button>
            {agendamentoAtivo && <button onClick={cancelarAgendamento} style={{ width:"100%",padding:"12px",borderRadius:12,border:"1px solid rgba(239,68,68,0.25)",background:"rgba(239,68,68,0.07)",color:"#f87171",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>Cancelar agendamento</button>}
          </div>
        </div>
      )}

      {/* Painel para quem */}
      {showParaQuem && (
        <div onClick={()=>setShowParaQuem(false)} style={{ position:"fixed",inset:0,zIndex:19999,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"flex-end",justifyContent:"center" }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:"100%",maxWidth:480,background:"#0d1527",borderRadius:"24px 24px 0 0",padding:"24px 24px 40px",border:"1px solid rgba(255,255,255,0.08)",borderBottom:"none",animation:"slideUp 0.3s ease" }}>
            <div style={{ width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 20px" }} />
            <p style={{ color:"#fff",fontWeight:800,fontSize:18,margin:"0 0 20px" }}>Para quem é a corrida?</p>
            <div onClick={()=>{setParaQuemLabel("Para mim");setShowParaQuem(false);}} style={{ display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:14,border:`1px solid ${paraQuemLabel==="Para mim"?"rgba(236,72,153,0.4)":"rgba(255,255,255,0.08)"}`,background:paraQuemLabel==="Para mim"?"rgba(236,72,153,0.07)":"rgba(255,255,255,0.03)",marginBottom:10,cursor:"pointer" }}>
              <div style={{ width:38,height:38,borderRadius:"50%",background:"rgba(255,255,255,0.06)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}><LuUserRound aria-hidden="true" /></div>
              <div style={{ flex:1 }}>
                <p style={{ fontWeight:700,fontSize:14,color:"#fff",margin:"0 0 2px" }}>Para mim</p>
                <p style={{ fontSize:11,color:"rgba(255,255,255,0.35)",margin:0 }}>Viagem para você mesmo</p>
              </div>
              {paraQuemLabel==="Para mim" && <LuCheck style={{ color:"#ec4899",fontSize:18 }} />}
            </div>
            <div style={{ padding:"14px 16px",borderRadius:14,border:"1px solid rgba(255,255,255,0.08)",background:"rgba(255,255,255,0.03)",marginBottom:14 }}>
              <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:12 }}>
                <div style={{ width:38,height:38,borderRadius:"50%",background:"rgba(255,255,255,0.06)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}><LuUsersRound /></div>
                <p style={{ fontWeight:700,fontSize:14,color:"#fff",margin:0 }}>Para outra pessoa</p>
              </div>
              <input type="text" placeholder="Nome da pessoa" value={nomeOutraPessoa} onChange={e=>setNomeOutraPessoa(e.target.value)} onClick={e=>e.stopPropagation()} style={{ width:"100%",padding:"11px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:13,boxSizing:"border-box",outline:"none" }} />
            </div>
            <button onClick={()=>{if(nomeOutraPessoa.trim())setParaQuemLabel("Para "+nomeOutraPessoa.trim().split(" ")[0]);setShowParaQuem(false);}} style={{ width:"100%",padding:"14px",borderRadius:12,border:"none",background:"linear-gradient(135deg,#ec4899,#7c3aed)",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>Confirmar</button>
          </div>
        </div>
      )}

      {/* Lista corridas agendadas */}
      {showAgendadas && corridasAgendadas.length > 0 && (
        <div style={{ position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-end",justifyContent:"center" }}>
          <div style={{ width:"100%",maxWidth:480,background:"#0d1527",borderRadius:"24px 24px 0 0",padding:"20px 20px 40px",border:"1px solid rgba(255,255,255,0.08)",borderBottom:"none",animation:"slideUp 0.3s ease",maxHeight:"80vh",overflow:"auto" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20 }}>
              <p style={{ color:"#fff",fontWeight:800,fontSize:18,margin:0 }}>Corridas agendadas</p>
              <button aria-label="Fechar" onClick={()=>setShowAgendadas(false)} style={{ width:32,height:32,borderRadius:"50%",border:"none",background:"rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.5)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}><LuX /></button>
            </div>
            {corridasAgendadas.map(item => (
              <div key={item.id} style={{ background:"rgba(255,255,255,0.04)",borderRadius:14,padding:"14px 16px",marginBottom:12,border:"1px solid rgba(255,255,255,0.07)" }}>
                <p style={{ color:"#a855f7",fontWeight:700,fontSize:13,margin:"0 0 4px",display:"flex",gap:6,alignItems:"center" }}><LuCalendarDays /> {formatarDataHoraBR(item.agendadoPara)}</p>
                <p style={{ color:"#fff",fontSize:14,fontWeight:600,margin:"0 0 2px" }}>{item.destino?.nome||"Destino"}</p>
                <div style={{ display:"flex",gap:8,color:"rgba(255,255,255,0.4)",fontSize:12,margin:"0 0 10px" }}>
                  <span>R$ {Number(item.valor||0).toFixed(2)}</span><span>•</span>
                  <span>{item.categoria === "elite" ? "Zuno Elite" : "Zuno Movel"}</span><span>•</span>
                  <span style={{display:"flex",gap:5,alignItems:"center"}}>{item.metodoPagamento === "pix" ? <><LuQrCode /> Pix</> : item.metodoPagamento === "maquininha" ? <><LuCreditCard /> Cartão</> : <><LuBanknote /> Dinheiro</>}</span>
                </div>
                <button onClick={() => handleCancelarAgendada(item)} style={{ width:"100%",padding:"10px",borderRadius:10,background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",color:"#ef4444",fontSize:13,fontWeight:700,cursor:"pointer" }}>
                  Cancelar agendamento
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {cityBlocked && <div role="dialog" aria-modal="true" aria-labelledby="city-blocked-title" style={{ position:"fixed", inset:0, zIndex:12000, background:"rgba(2,8,20,.78)", display:"grid", placeItems:"center", padding:20 }}>
        <div style={{ width:"min(100%,430px)", background:"#0d1930", border:"1px solid rgba(38,224,194,.25)", borderRadius:24, padding:26, color:"#f5fbff", boxShadow:"0 30px 90px rgba(0,0,0,.5)" }}>
          <small style={{ color:"#26e0c2", fontWeight:800, letterSpacing:".1em" }}>EXPANSÃO ZUNO</small>
          <h2 id="city-blocked-title" style={{ margin:"10px 0" }}>Zuno está chegando à sua cidade</h2>
          <p style={{ color:"#b8c7dc", lineHeight:1.6 }}>{cityBlocked.userMessage || "Estamos preparando a operação nesta região. Você pode continuar explorando o aplicativo."}</p>
          <button onClick={async () => {
            await joinCityInterest({ cityName: cityBlocked.city?.name || cidadeDetectada, origin: posicaoAtualRef.current, notificationsRequested: false }).catch(() => null);
            mostrarNotif("interesse", "Interesse registrado", "Avisaremos quando a operação estiver disponível.", "#26e0c2");
            setCityBlocked(null);
          }} style={{ width:"100%", minHeight:50, border:0, borderRadius:15, background:"linear-gradient(135deg,#20d8bd,#3e7cff)", color:"#031426", fontWeight:850, cursor:"pointer" }}>Entrar na lista de interesse</button>
          <button onClick={() => setCityBlocked(null)} style={{ width:"100%", minHeight:46, border:0, background:"transparent", color:"#dce8f8", fontWeight:700, cursor:"pointer" }}>Continuar explorando</button>
        </div>
      </div>}

      {/* Painel destino aberto */}
      {openPainel && (
        <div
          className="zuno-destination-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ride-choice-title"
          data-address-search={addressSearchActive ? "true" : "false"}
          data-keyboard-open={addressViewport.keyboardOpen ? "true" : "false"}
          style={{
            "--zuno-visible-viewport-height": `${addressViewport.height}px`,
            "--zuno-viewport-offset-top": `${addressViewport.offsetTop}px`,
          }}
        >
          <section className="zuno-ride-choice-sheet">
            <div className="zuno-sheet-handle" aria-hidden="true" />
            <div className="zuno-destination-header">
              <h3 id="ride-choice-title">{agendamentoAtivo ? `Agendar para ${agendadoLabel}` : "Para onde vamos?"}</h3>
              <button type="button" aria-label="Fechar escolha da corrida" onClick={() => setOpenPainel(false)}><LuX /></button>
            </div>
            <div className="zuno-ride-choice-scroll">
              <EscolhaDestino setDestino={handleDestinoConfirmado} agendadoLabel={agendadoLabel} paraQuemLabel={paraQuemLabel} onOpenAgendamento={() => setShowAgendamento(true)} onOpenParaQuem={() => setShowParaQuem(true)} onEscolherNoMapa={handleAbrirEscolherNoMapa} destinoPreSelecionado={destinoDoMapa} onDestinoPreSelecionadoUsado={() => setDestinoDoMapa(null)} enderecoGps={enderecoGpsLabel} cidadeAtual={cidadeDetectada} userPosition={hasLocation ? posicaoAtualRef.current : null} locationStatus={locationStatus} onRetryLocation={retryCurrentLocation} onSearchInteractionChange={setAddressSearchActive} />
            </div>
          </section>
        </div>
      )}

      {/* ── Modo Escolher no Mapa ── */}
      {modoEscolherNoMapa && (
        <div className="zuno-map-picker" style={{ position:"fixed",inset:0,zIndex:10000 }}>
          {/* Mapa interativo de fundo */}
          <MapEscolhedor
            userPos={posicaoAtualRef.current}
            onCenterChange={handlePinCenterChange}
          />
          {/* Pin central fixo */}
          <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-100%)",zIndex:2,pointerEvents:"none" }}>
            <svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg" style={{ filter:"drop-shadow(0 4px 8px rgba(0,0,0,0.4))" }}>
              <path d="M18 0C8.059 0 0 8.059 0 18c0 12 18 30 18 30S36 30 36 18C36 8.059 27.941 0 18 0z" fill="#a855f7"/>
              <circle cx="18" cy="18" r="8" fill="white" opacity="0.95"/>
              <circle cx="18" cy="18" r="4" fill="#a855f7"/>
            </svg>
            <div style={{ width:8,height:8,borderRadius:"50%",background:"rgba(0,0,0,0.2)",margin:"-2px auto 0",transform:"scaleX(2)" }}/>
          </div>
          {/* Header */}
          <div className="zuno-map-picker-header" style={{ position:"absolute",top:0,left:0,right:0,padding:"48px 16px 14px",background:"linear-gradient(to bottom,rgba(10,14,26,0.95),transparent)",zIndex:3 }}>
            <div style={{ display:"flex",alignItems:"center",gap:12 }}>
              <button className="zuno-map-picker-back" aria-label="Voltar" onClick={()=>setModoEscolherNoMapa(false)} style={{ width:36,height:36,borderRadius:"50%",border:"none",background:"rgba(255,255,255,0.1)",color:"#fff",fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>←</button>
              <p className="zuno-map-picker-title" style={{ color:"#fff",fontWeight:700,fontSize:15,margin:0 }}>Mova o mapa e fixe o pino no destino</p>
            </div>
          </div>
          {/* Card do endereço detectado */}
          <div style={{ position:"absolute",bottom:148,left:16,right:16,zIndex:3 }}>
            <div className="zuno-map-picker-address" aria-live="polite" aria-busy={geocodingPin} style={{ background:"rgba(13,21,39,0.97)",backdropFilter:"blur(16px)",borderRadius:16,padding:"14px 16px",border:"1px solid rgba(168,85,247,0.25)",boxShadow:"0 4px 24px rgba(0,0,0,0.5)",minHeight:58,display:"flex",alignItems:"center",gap:12 }}>
              <div style={{ width:36,height:36,borderRadius:"50%",background:"rgba(168,85,247,0.15)",border:"1px solid rgba(168,85,247,0.3)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                {geocodingPin
                  ? <div style={{ width:16,height:16,borderRadius:"50%",border:"2px solid rgba(168,85,247,0.2)",borderTopColor:"#a855f7",animation:"spin .7s linear infinite" }}/>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                }
              </div>
              {geocodingPin && !enderecoPin ? (
                <p style={{color:"rgba(255,255,255,0.4)",fontSize:13,margin:0}}>Identificando endereço...</p>
              ) : enderecoPin ? (
                <div style={{flex:1,minWidth:0}}>
                  <p style={{color:"#fff",fontWeight:800,fontSize:14,margin:0,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{enderecoPin.nome}</p>
                  <p style={{color:"rgba(255,255,255,0.4)",fontSize:11,margin:"3px 0 0",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{enderecoPin.endereco}</p>
                  {geocodingPin && <small className="zuno-map-picker-updating">Atualizando localização...</small>}
                </div>
              ) : (
                <p style={{color:"rgba(255,255,255,0.35)",fontSize:13,margin:0}}>Mova o mapa para o local</p>
              )}
            </div>
          </div>
          {/* Botão confirmar */}
          <div style={{ position:"absolute",bottom:80,left:0,right:0,padding:"0 16px",zIndex:3 }}>
            <button className="zuno-map-picker-confirm"
              onClick={handleConfirmarPinNoMapa}
              disabled={geocodingPin}
              style={{ width:"100%",padding:"16px",borderRadius:16,border:"none",background: geocodingPin ? "rgba(124,58,237,0.4)" : "linear-gradient(135deg,#7c3aed,#a855f7)",color:"#fff",fontSize:15,fontWeight:800,cursor: geocodingPin ? "not-allowed" : "pointer",fontFamily:"inherit",boxShadow: geocodingPin ? "none" : "0 4px 20px rgba(124,58,237,0.5)",transition:"all 0.2s" }}
            >
              {geocodingPin ? "Aguarde..." : enderecoPin ? `Confirmar: ${enderecoPin.nome}` : "Confirmar este local"}
            </button>
          </div>
        </div>
      )}

      {/* Mapa — agora recebe motoristaPosicao */}
      <div className="zuno-home-map-stage" style={{ position:"absolute",inset:0,zIndex:1 }}>
        <Map
          destino={destino}
          onRouteCalculated={handleRouteCalculated}
          corridaConfirmada={corridaConfirmada}
          userLocation={posicaoAtualRef.current}
          hasPermission={hasLocation}
          onRequestLocation={getLocation}
          motoristaPosicao={motoristaPosicao}           // ← NOVO
          corridaStatus={corridaStatus}                 // ← NOVO
          routeEncodedPolyline={routeEncodedPolyline}
          showControls={!isIdleHome}
          forceDark={isIdleHome && theme === "dark"}
        />
      </div>
      {/* Home do passageiro — composição fiel à referência */}
      {isIdleHome && (
        <main className="zuno-reference-home">
          <header className="zuno-reference-header">
            <div className="zuno-reference-brand">
              <button type="button" aria-label="Abrir menu" onClick={() => navigate("/conta")}><LuMenu /></button>
              <img src={zunoSymbol} alt="Zuno" />
            </div>
            <div className="zuno-reference-profile">
              <div className="zuno-reference-greeting">
                <strong>{saudacao()}, {primeiroNome}</strong>
                <span>Passageiro</span>
              </div>
              <button className="zuno-reference-bell" type="button" aria-label="Notificações" onClick={() => navigate("/atividade")}>
                <LuBell />
                {corridasAgendadas.length > 0 && <i aria-hidden="true" />}
              </button>
              <button className="zuno-reference-avatar-button" type="button" aria-label="Abrir conta" onClick={() => navigate("/conta")}>
                <PassengerAvatar photoURL={fotoUsuario} name={usuario || primeiroNome} />
              </button>
            </div>
          </header>

          <section className="zuno-reference-search" aria-label="Definir trajeto">
            <div className="zuno-reference-route-markers" aria-hidden="true"><i /><span /><span /><span /><b /></div>
            <div className="zuno-reference-fields">
              <button type="button" className="zuno-reference-origin" onClick={() => setOpenPainel(true)}>{origemExibida}</button>
              <div className="zuno-reference-destination-row">
                <button type="button" className="zuno-reference-destination" onClick={() => setOpenPainel(true)}>Para onde vamos?</button>
                <button type="button" className="zuno-reference-schedule" onClick={() => setShowAgendamento(true)} aria-label="Definir horário da corrida">
                  <LuCalendarDays /><span>{agendamentoAtivo ? agendadoLabel : "Agora"}</span><LuChevronDown />
                </button>
              </div>
            </div>
            <button className="zuno-reference-crosshair" type="button" aria-label="Usar minha localização" onClick={getLocation}><LuCrosshair /></button>
          </section>

          <div className="zuno-reference-map-spacer" aria-hidden="true" />

          <section className="zuno-reference-promos" aria-label="Destaques Zuno">
            <button className="zuno-driver-partner-card" type="button" onClick={() => navigate("/cadastro-motorista")}>
              <img src={lightReferenceHome ? driverPartnerLightBanner : driverPartnerBanner} alt={lightReferenceHome ? "Seja um motorista parceiro. Ganhe mais com a Zuno. Saiba mais" : ""} aria-hidden={lightReferenceHome ? undefined : "true"} />
              {!lightReferenceHome && <span className="zuno-driver-partner-copy"><strong>Seja um motorista parceiro</strong><small>Ganhe mais com a Zuno</small></span>}
            </button>
            <HomePromoCarousel campaigns={homeCampaigns} />
          </section>
        </main>
      )}

      {/* Painel buscando motorista */}
      {buscando && !dadosMotorista && (
        <div className="zuno-search-sheet" style={{ position:"absolute",bottom:70,left:0,right:0,background:"#0a0e1a",borderRadius:"24px 24px 0 0",padding:"20px 20px 24px",zIndex:50,boxShadow:"0 -4px 30px rgba(0,0,0,0.4)",animation:"fadeUp .3s ease",border:"1px solid rgba(255,255,255,0.06)",borderBottom:"none" }}>
          <div style={{ width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 16px" }} />
          <div style={{ display:"flex",alignItems:"center",gap:16,marginBottom:16 }}>
            <div style={{ position:"relative",width:58,height:58,flexShrink:0 }}>
              <div style={{ position:"absolute",inset:0,borderRadius:"50%",background:"rgba(236,72,153,0.25)",animation:"radar 2s ease-out infinite" }} />
              <div style={{ position:"absolute",inset:0,borderRadius:"50%",background:"rgba(124,58,237,0.15)",animation:"radar 2s ease-out infinite .7s" }} />
              <div className="zuno-search-vehicle" style={{ position:"relative",width:"100%",height:"100%",borderRadius:"50%",background:"#111a32",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1,overflow:"hidden",border:"1px solid #7657ff" }}><ZunoVehicle type={destino?.categoria || "moto"} /></div>
            </div>
            <div>
              <p className="zuno-search-title" style={{ color:"#fff",fontWeight:700,fontSize:15,margin:"0 0 3px" }}>Buscando motorista{dots}</p>
              <p className="zuno-search-subtitle" style={{ color:"rgba(255,255,255,0.4)",fontSize:12,margin:0 }}>Estamos localizando um motorista próximo.</p>
            </div>
          </div>
          {destino && (
            <div className="zuno-search-summary" style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"rgba(255,255,255,0.04)",borderRadius:10,marginBottom:12,border:"1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ width:7,height:7,background:"#ef4444",borderRadius:2,flexShrink:0 }} />
              <p style={{ color:"rgba(255,255,255,0.6)",fontSize:12,margin:0,flex:1 }}>{destino.name||"Destino"}</p>
              <p style={{ color:"#22c55e",fontWeight:700,fontSize:13,margin:0 }}>R$ {Number(valorExibido||0).toFixed(2)}</p>
              <span style={{ color:"rgba(255,255,255,0.4)",fontSize:11 }}>{iconePag(pagamentoSelecionado?.metodo)}</span>
            </div>
          )}
          <button onClick={handleCancelar} style={{ width:"100%",padding:"13px",borderRadius:12,border:"1px solid rgba(239,68,68,0.25)",background:"rgba(239,68,68,0.07)",color:"#f87171",fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit" }}>Cancelar solicitação</button>
        </div>
      )}

      {/* Painel motorista encontrado */}
      {dadosMotorista && (corridaStatus === "accepted" || corridaStatus === "arrived" || corridaStatus === "in_progress") && (
        <div className="zuno-driver-sheet" style={{ position:"absolute",bottom:70,left:0,right:0,background:"#0a0e1a",borderRadius:"24px 24px 0 0",padding:"16px 20px 28px",zIndex:50,boxShadow:"0 -4px 30px rgba(0,0,0,0.4)",animation:"slideUp 0.3s ease",border:"1px solid rgba(255,255,255,0.06)",borderBottom:"none" }}>
          <div style={{ width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"0 auto 14px" }} />

          {/* Status label */}
          <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
            <div style={{ width:8,height:8,borderRadius:"50%",
              background: corridaStatus==="arrived" ? "#f59e0b" : corridaStatus==="in_progress" ? "#a855f7" : "#22c55e",
              animation:"pulse 1.5s ease-in-out infinite" }} />
            <p style={{ fontWeight:700,fontSize:13,margin:0,
              color: corridaStatus==="arrived" ? "#f59e0b" : corridaStatus==="in_progress" ? "#a855f7" : "#22c55e" }}>
              {corridaStatus==="accepted" ? "Motorista a caminho" : corridaStatus==="arrived" ? "Motorista chegou. Vá ao veículo" : "Corrida em andamento"}
            </p>
          </div>

          {/* Barra de distância + ETA (só no accepted) */}
          {corridaStatus === "accepted" && kmParaMim !== null && (
            <div style={{ marginBottom:14 }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
                <span style={{ fontSize:11,color:"rgba(255,255,255,0.35)" }}>Motorista se aproximando</span>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <span style={{ fontSize:12,color:"#22c55e",fontWeight:700 }}>{kmParaMim} km</span>
                  {etaMin !== null && (
                    <span style={{ fontSize:12,color:"#a855f7",fontWeight:700,background:"rgba(168,85,247,0.1)",padding:"2px 8px",borderRadius:20,border:"1px solid rgba(168,85,247,0.25)" }}>
                      ~{etaMin} min
                    </span>
                  )}
                </div>
              </div>
              <div style={{ height:5,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden" }}>
                <div style={{ height:"100%",borderRadius:3,background:"linear-gradient(90deg,#22c55e88,#22c55e)",width:`${Math.max(5,Math.min(95,100-(kmParaMim*25)))}%`,transition:"width 3s ease" }} />
              </div>
            </div>
          )}

          {/* Barra in_progress */}
          {corridaStatus === "in_progress" && (
            <div style={{ marginBottom:14,padding:"10px 14px",background:"rgba(168,85,247,0.07)",borderRadius:12,border:"1px solid rgba(168,85,247,0.15)" }}>
              <div style={{ display:"flex",justifyContent:"space-between",marginBottom:6 }}>
                <span style={{ fontSize:11,color:"rgba(255,255,255,0.4)" }}>Em rota ao destino</span>
                <span style={{ fontSize:12,color:"#a855f7",fontWeight:700 }}>{progressoCorrida}% • {destino?.name||"Destino"}</span>
              </div>
              <div style={{ height:5,background:"rgba(255,255,255,0.06)",borderRadius:3,overflow:"hidden" }}>
                <div style={{ height:"100%",borderRadius:3,background:"linear-gradient(90deg,#a855f788,#a855f7)",width:`${Math.max(5,progressoCorrida)}%`,transition:"width 2s ease" }} />
              </div>
            </div>
          )}

          {/* Card motorista estilo Uber */}
          <div style={{ marginBottom:14,background:"rgba(255,255,255,0.04)",borderRadius:14,overflow:"hidden",border:"1px solid rgba(255,255,255,0.07)" }}>
            {/* Linha superior: foto + nome + avaliação + botões */}
            <div style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 14px 10px" }}>
              <div style={{ width:46,height:46,borderRadius:"50%",overflow:"hidden",flexShrink:0 }}>
                {dadosMotorista.foto
                  ? <img src={dadosMotorista.foto} alt="motorista" style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                  : <div style={{ width:"100%",height:"100%",background:"linear-gradient(135deg,#7c3aed,#a855f7)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:18 }}>{(dadosMotorista.nome||"M").charAt(0)}</div>
                }
              </div>
              <div style={{ flex:1 }}>
                <p style={{ color:"#fff",fontWeight:700,fontSize:14,margin:"0 0 1px" }}>{dadosMotorista.nome}</p>
                <p style={{ color:"#f59e0b",fontSize:12,margin:0,fontWeight:600 }}>⭐ {Number(dadosMotorista.avaliacao||5.0).toFixed(1)}</p>
              </div>
              <div style={{ display:"flex",gap:6 }}>
                <button onClick={()=>setShowChat(true)} style={{ padding:"8px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.04)",color:"rgba(255,255,255,0.6)",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                  <LuMessageCircle aria-label="Conversar com motorista" />
                </button>
                <button onClick={handleCompartilharViagem} style={{ padding:"8px 12px",borderRadius:10,border:"1px solid rgba(168,85,247,0.25)",background:"rgba(168,85,247,0.08)",color:"#a855f7",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                  <LuLink aria-label="Copiar link da corrida" />
                </button>
                <button onClick={() => {
                  if (!dadosMotorista?.id) return;
                  const favs = JSON.parse(localStorage.getItem("motoristasFavoritos") || "[]");
                  if (!favs.find(f => f.id === dadosMotorista.id)) {
                    favs.unshift({ id: dadosMotorista.id, nome: dadosMotorista.nome, foto: dadosMotorista.foto || null, avaliacao: dadosMotorista.avaliacao || 5 });
                    localStorage.setItem("motoristasFavoritos", JSON.stringify(favs.slice(0, 20)));
                  }
                  mostrarNotif("fav", "Motorista favoritado", `${dadosMotorista.nome} foi salvo nos seus favoritos.`, "#f59e0b");
                }} style={{ padding:"8px 12px",borderRadius:10,border:"1px solid rgba(245,158,11,0.25)",background:"rgba(245,158,11,0.08)",color:"#f59e0b",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                  <LuStar aria-label="Favoritar motorista" />
                </button>
                <button onClick={() => setShowFareSplit(true)} style={{ padding:"8px 12px",borderRadius:10,border:"1px solid rgba(6,182,212,0.25)",background:"rgba(6,182,212,0.08)",color:"#06b6d4",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                  <LuScissors aria-label="Dividir corrida" />
                </button>
              </div>
            </div>
            {/* Linha inferior: silhueta + info veículo */}
            <div style={{ display:"flex",alignItems:"center",gap:0,background:"rgba(255,255,255,0.03)",borderTop:"1px solid rgba(255,255,255,0.05)",padding:"10px 14px" }}>
              {/* Silhueta SVG do veículo */}
              <div style={{ width:96,height:64,flexShrink:0,marginRight:12 }}>
                <ZunoVehicle type={dadosMotorista.tipoVeiculo === "moto" ? "moto" : destino?.categoria || "movel"} />
              </div>
              {/* Info textual */}
              <div style={{ flex:1 }}>
                <p style={{ color:"#fff",fontWeight:700,fontSize:13,margin:"0 0 3px" }}>
                  {dadosMotorista.marca} {dadosMotorista.modelo}
                  {dadosMotorista.cor && <span style={{ color:"rgba(255,255,255,0.4)",fontWeight:400 }}> • {dadosMotorista.cor}</span>}
                </p>
                {dadosMotorista.placa && (
                  <div style={{ display:"inline-flex",alignItems:"center",background:"rgba(255,255,255,0.07)",border:"1.5px solid rgba(255,255,255,0.15)",borderRadius:6,padding:"3px 10px" }}>
                    <span style={{ color:"#fff",fontWeight:800,fontSize:14,letterSpacing:2,fontFamily:"monospace" }}>{dadosMotorista.placa}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pix: chave + botão QR durante corrida */}
          {dadosMotorista.pixMotorista && dadosMotorista.metodoPagamento === "pix" && (
            <div style={{ marginBottom:12 }}>
              <div style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"rgba(6,182,212,0.06)",borderRadius:12,border:"1px solid rgba(6,182,212,0.15)",marginBottom:8 }}>
                <LuQrCode aria-hidden="true" style={{ fontSize:18,color:"#18d7e5" }} />
                <div style={{ flex:1 }}>
                  <p style={{ color:"rgba(255,255,255,0.35)",fontSize:10,letterSpacing:0.8,margin:"0 0 2px",textTransform:"uppercase" }}>Chave Pix</p>
                  <p style={{ color:"#06b6d4",fontSize:12,fontWeight:700,margin:0 }}>{dadosMotorista.pixMotorista}</p>
                </div>
                <button onClick={()=>{navigator.clipboard?.writeText(dadosMotorista.pixMotorista).catch(()=>{});mostrarNotif("pix","Chave Pix copiada!","","#06b6d4");}} style={{ flexShrink:0,padding:"6px 12px",borderRadius:8,border:"1px solid rgba(6,182,212,0.3)",background:"rgba(6,182,212,0.1)",color:"#06b6d4",fontSize:12,fontWeight:700,cursor:"pointer" }}>Copiar</button>
              </div>
              <button
                onClick={() => setShowModalPix(true)}
                style={{ width:"100%",padding:"10px",borderRadius:10,border:"1px solid rgba(6,182,212,0.25)",background:"rgba(6,182,212,0.06)",color:"#06b6d4",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}
              >
                <LuQrCode style={{verticalAlign:"middle",marginRight:6}} /> Ver QR Code para pagar
              </button>
            </div>
          )}

          {/* Compartilhar corrida (in_progress) */}
          {corridaStatus === "in_progress" && (
            <button
              onClick={() => {
                const url = posicaoAtualRef.current
                  ? `https://maps.google.com/maps?q=${posicaoAtualRef.current.lat},${posicaoAtualRef.current.lng}`
                  : "";
                const texto = `Estou em uma corrida Zuno\nDestino: ${destino?.name || "Destino"}\nAcompanhe: ${url}`;
                if (navigator.share) navigator.share({ title: "Minha corrida Zuno", text: texto }).catch(()=>{});
                else navigator.clipboard?.writeText(texto).then(() => mostrarNotif("share","Copiado","Link da corrida copiado.","#a855f7")).catch(()=>{});
              }}
              style={{ width:"100%",padding:"11px",borderRadius:12,border:"1px solid rgba(168,85,247,0.2)",background:"rgba(168,85,247,0.07)",color:"#a855f7",fontWeight:600,fontSize:13,cursor:"pointer",fontFamily:"inherit",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}
            >
              <LuShare2 style={{verticalAlign:"middle",marginRight:6}} /> Compartilhar corrida
            </button>
          )}

          {/* Cancelar (só no accepted/arrived) */}
          {(corridaStatus === "accepted" || corridaStatus === "arrived") && (
            <button onClick={handleCancelar} style={{ width:"100%",padding:"13px",borderRadius:12,border:"1px solid rgba(239,68,68,0.25)",background:"rgba(239,68,68,0.07)",color:"#f87171",fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:"inherit" }}>
              Cancelar corrida
            </button>
          )}
        </div>
      )}

      {/* Selector de pagamento */}
      {showPayment && (
        <PaymentSelector
          valorCorrida={destinoPendente?.dadosCorrida?.finalPrice || 0}
          categoria={destinoPendente?.categoria || "moto"}
          distanciaKm={destinoPendente?.dadosCorrida?.distanciaKm || destinoPendente?.distanciaKm}
          duracaoMin={destinoPendente?.dadosCorrida?.tempoMin || destinoPendente?.tempoMin}
          onConfirmar={handlePagamentoConfirmado}
          onVoltar={() => setShowPayment(false)}
        />
      )}

      {/* Chat */}
      {showChat && rideId && (
        <ChatCorrida rideId={rideId} remetenteTipo="passenger" onClose={() => setShowChat(false)} />
      )}

      {/* Rating */}
      {showRating && corridaCompletada && (
        <RatingModal
          tipo="motorista"
          nome={corridaCompletada.motoristaNome}
          foto={corridaCompletada.motoristaFoto}
          rideId={corridaCompletada.rideId}
          reportadoPorId={userUid}
          onSubmit={handleSubmitRating}
          onClose={() => { setShowRating(false); setCorridaCompletada(null); }}
        />
      )}

      {/* Modal motorista chegou */}
      {showModalChegou && dadosMotorista && (
        <ModalMotoristaChegou
          motoristaNome={dadosMotorista.nome}
          placa={dadosMotorista.placa}
          onFechar={() => setShowModalChegou(false)}
        />
      )}

      {/* Modal pagamento ao final da corrida */}
      {showModalPagamento && dadosPagamento && (
        <ModalPagamento
          dados={dadosPagamento}
          onFechar={() => {
            setShowModalPagamento(false);
            // Se pix já foi pago antecipado, pula direto para avaliação
            if (dadosPagamento.pixJaPago) {
              setTimeout(() => setShowRating(true), 300);
            } else {
              setTimeout(() => setShowRating(true), 400);
            }
          }}
        />
      )}

      {/* QR Code Pix durante a corrida (botão manual) */}
      {showModalPix && dadosMotorista && (
        <ModalPixCorrida
          chavePix={dadosMotorista.pixMotorista}
          qrCodeUrl={dadosMotorista.qrCodePix}
          valorCorrida={valorExibido}
          motoristaNome={dadosMotorista.nome}
          onFechar={() => setShowModalPix(false)}
        />
      )}

      {/* ── NOVO: Popup "pagar Pix agora ou ao final?" ── */}
      {showPixAntecipado && dadosMotorista && (
        <ModalPixAntecipado
          motoristaNome={dadosMotorista.nome}
          chavePix={dadosMotorista.pixMotorista}
          qrCodeUrl={dadosMotorista.qrCodePix}
          valorCorrida={valorExibido}
          onPagarAgora={handlePixAberto}
          onPagarFinal={handlePixPagarFinal}
        />
      )}

      {/* Modal dividir corrida (fare split) */}
      {showFareSplit && (
        <div style={{ position:"fixed",inset:0,zIndex:99990,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(6px)",display:"flex",alignItems:"flex-end" }}>
          <div style={{ width:"100%",maxWidth:480,margin:"0 auto",background:"linear-gradient(160deg,#0d0f2b,#0a0e1a)",borderRadius:"28px 28px 0 0",padding:"20px 20px 40px",border:"1px solid rgba(255,255,255,0.07)",borderBottom:"none",animation:"slideUp 0.3s ease" }}>
            <div style={{ width:40,height:4,background:"rgba(255,255,255,0.12)",borderRadius:2,margin:"0 auto 20px" }}/>
            <h3 style={{ color:"#fff",fontSize:17,fontWeight:800,margin:"0 0 4px",textAlign:"center",display:"flex",gap:8,justifyContent:"center",alignItems:"center" }}><LuScissors /> Dividir corrida</h3>
            <p style={{ color:"rgba(255,255,255,0.35)",fontSize:12,margin:"0 0 24px",textAlign:"center" }}>Divida o valor com quem viaja com você</p>

            {/* Valor total */}
            <div style={{ background:"rgba(255,255,255,0.04)",borderRadius:14,padding:"14px",marginBottom:20,textAlign:"center",border:"1px solid rgba(255,255,255,0.07)" }}>
              <p style={{ color:"rgba(255,255,255,0.4)",fontSize:11,letterSpacing:1,textTransform:"uppercase",margin:"0 0 4px" }}>Valor total da corrida</p>
              <p style={{ color:"#fff",fontSize:26,fontWeight:900,margin:0,letterSpacing:-0.5 }}>R$ {Number(valorExibido||0).toFixed(2)}</p>
            </div>

            {/* Seletor de pessoas */}
            <p style={{ color:"rgba(255,255,255,0.4)",fontSize:12,margin:"0 0 12px",textAlign:"center" }}>Número de pessoas</p>
            <div style={{ display:"flex",justifyContent:"center",gap:12,marginBottom:20 }}>
              {[2,3,4].map(n => (
                <button key={n} onClick={() => setSplitPessoas(n)}
                  style={{ width:56,height:56,borderRadius:"50%",border:`2px solid ${splitPessoas===n?"#06b6d4":"rgba(255,255,255,0.1)"}`,background:splitPessoas===n?"rgba(6,182,212,0.15)":"rgba(255,255,255,0.04)",color:splitPessoas===n?"#06b6d4":"rgba(255,255,255,0.5)",fontSize:18,fontWeight:800,cursor:"pointer" }}>
                  {n}
                </button>
              ))}
            </div>

            {/* Valor por pessoa */}
            <div style={{ background:"rgba(6,182,212,0.08)",border:"1px solid rgba(6,182,212,0.2)",borderRadius:14,padding:"14px",marginBottom:20,textAlign:"center" }}>
              <p style={{ color:"rgba(255,255,255,0.4)",fontSize:11,letterSpacing:1,textTransform:"uppercase",margin:"0 0 4px" }}>Cada pessoa paga</p>
              <p style={{ color:"#06b6d4",fontSize:28,fontWeight:900,margin:0,letterSpacing:-0.5 }}>R$ {(Number(valorExibido||0)/splitPessoas).toFixed(2)}</p>
            </div>

            {/* Compartilhar */}
            <button onClick={() => {
              const valor = (Number(valorExibido||0)/splitPessoas).toFixed(2);
              const txt = `Você deve R$ ${valor} da corrida Zuno\nChave Pix: ${dadosMotorista?.pixMotorista || "(veja com o motorista)"}`;
              if (navigator.share) navigator.share({ title:"Dividir corrida Zuno", text:txt }).catch(()=>{});
              else { navigator.clipboard?.writeText(txt).catch(()=>{}); mostrarNotif("split","Copiado","Valor por pessoa copiado para a área de transferência.","#06b6d4"); }
            }}
              style={{ width:"100%",padding:"14px",borderRadius:50,border:"none",background:"linear-gradient(135deg,#0891b2,#06b6d4)",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:10,fontFamily:"inherit" }}>
              <LuShare2 style={{verticalAlign:"middle",marginRight:6}} /> Compartilhar valor por pessoa
            </button>
            <button onClick={() => setShowFareSplit(false)}
              style={{ width:"100%",padding:"12px",borderRadius:50,border:"none",background:"transparent",color:"rgba(255,255,255,0.25)",fontSize:13,cursor:"pointer" }}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Botão SOS — aparece durante corridas ativas */}
      {(corridaStatus === "accepted" || corridaStatus === "arrived" || corridaStatus === "in_progress") && (
        <SOSButton
          rideId={rideId}
          userType="passenger"
          lat={posicaoAtualRef.current?.lat}
          lng={posicaoAtualRef.current?.lng}
        />
      )}

      {/* Modal aviso de cancelamento após aceite */}
      {showAvisoCancelar && (
        <div style={{ position:"fixed",inset:0,zIndex:99999,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(6px)",display:"flex",alignItems:"flex-end",justifyContent:"center" }}>
          <div style={{ width:"100%",maxWidth:480,background:"linear-gradient(160deg,#1a0a0a,#0a0e1a)",borderRadius:"24px 24px 0 0",padding:"24px 20px 40px",border:"1px solid rgba(239,68,68,0.2)",borderBottom:"none",animation:"slideUp 0.3s ease" }}>
            <div style={{ width:36,height:4,background:"rgba(255,255,255,0.12)",borderRadius:2,margin:"0 auto 20px" }} />
            <div style={{ textAlign:"center",marginBottom:16 }}>
              <LuCircleAlert aria-hidden="true" style={{ fontSize:38,marginBottom:8,color:"#ff5c6c" }} />
              <p style={{ color:"#fff",fontWeight:800,fontSize:17,margin:"0 0 6px" }}>Cancelar a corrida?</p>
              <p style={{ color:"rgba(255,255,255,0.4)",fontSize:13,margin:0,lineHeight:1.5 }}>
                {corridaStatus==="arrived"
                  ? "O motorista já chegou ao local. Cancele apenas se realmente necessário."
                  : "O motorista já aceitou e está a caminho."}
              </p>
            </div>

            {/* Política de cancelamento */}
            <div style={{ background:"rgba(239,68,68,0.07)",border:"1px solid rgba(239,68,68,0.18)",borderRadius:14,padding:"12px 14px",marginBottom:18 }}>
              <p style={{ color:"rgba(255,255,255,0.5)",fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",margin:"0 0 8px" }}>Política de cancelamento</p>
              <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ width:6,height:6,borderRadius:"50%",background:"#22c55e",flexShrink:0 }} />
                  <p style={{ color:"rgba(255,255,255,0.55)",fontSize:12,margin:0 }}>Grátis se cancelar <strong style={{color:"#fff"}}>antes</strong> do motorista aceitar</p>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ width:6,height:6,borderRadius:"50%",background:"#f59e0b",flexShrink:0 }} />
                  <p style={{ color:"rgba(255,255,255,0.55)",fontSize:12,margin:0 }}>2 cancelamentos/semana após aceite podem <strong style={{color:"#f59e0b"}}>suspender</strong> sua conta</p>
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ width:6,height:6,borderRadius:"50%",background:"#ef4444",flexShrink:0 }} />
                  <p style={{ color:"rgba(255,255,255,0.55)",fontSize:12,margin:0 }}>O motorista pode ser prejudicado e perder tempo de deslocamento</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => executarCancelamento("passageiro_cancelou_apos_aceite")}
              style={{ width:"100%",padding:"14px",borderRadius:14,border:"1px solid rgba(239,68,68,0.3)",background:"rgba(239,68,68,0.12)",color:"#f87171",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:10 }}
            >
              Sim, cancelar corrida
            </button>
            <button
              onClick={() => setShowAvisoCancelar(false)}
              style={{ width:"100%",padding:"13px",borderRadius:14,border:"none",background:"linear-gradient(135deg,#7c3aed,#a855f7)",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}
            >
              Manter corrida
            </button>
          </div>
        </div>
      )}

      {!buscando && !dadosMotorista && !openPainel && <BottomNav active="inicio" />}
    </div>
  );
}

export default PassengerHome;
